(function () {
  function parseInstanceIdFromBundleId(bundleId, bundleGroup) {
    if (!bundleId) return null;
    if (bundleGroup && bundleId.startsWith(`${bundleGroup}-`)) {
      return bundleId.slice(bundleGroup.length + 1);
    }
    const idx = bundleId.indexOf('-');
    if (idx === -1) return null;
    return bundleId.slice(idx + 1);
  }

  function isBundleAnchorLine(line) {
    const props = line.properties || {};
    if (props._BundleGift) return false;
    if (props._BundleId || props._BundleTitle) return true;
    const parentVariantId = props._BundleParentVariantId;
    if (!parentVariantId) return false;
    return String(line.variant_id) === String(parentVariantId);
  }

  function getRelatedBundleLineKeys(cartItems, removedLine) {
    if (!isBundleAnchorLine(removedLine)) return null;

    const props = removedLine.properties || {};
    const bundleId = props._BundleId;
    const instanceId =
      props._BundleInstance ||
      parseInstanceIdFromBundleId(bundleId, props._BundleGroup);

    if (!bundleId && !instanceId) return null;

    const keys = [];
    cartItems.forEach((item) => {
      const itemProps = item.properties || {};
      if (bundleId && itemProps._BundleId === bundleId) {
        keys.push(item.key);
        return;
      }
      if (instanceId && itemProps._BundleInstance === instanceId) {
        keys.push(item.key);
      }
    });

    return keys.length ? keys : null;
  }

  function isProbeSetParentLine(line) {
    const props = line.properties || {};
    return Boolean(props._ProbeSetInstance && !props._ProbeSetGift);
  }

  function getRelatedProbeSetLineKeys(cartItems, removedLine) {
    if (!isProbeSetParentLine(removedLine)) return null;

    const instanceId = removedLine.properties._ProbeSetInstance;
    if (!instanceId) return null;

    const keys = cartItems
      .filter((item) => item.properties?._ProbeSetInstance === instanceId)
      .map((item) => item.key);

    return keys.length ? keys : null;
  }

  function getRelatedLineKeys(cartItems, removedLine) {
    return getRelatedBundleLineKeys(cartItems, removedLine) || getRelatedProbeSetLineKeys(cartItems, removedLine);
  }

  function bundleRemovalLine(cartItems, line) {
    if (!isNestedCartLine(line)) return line;
    const parentKey = line.parent_relationship?.parent_key;
    return cartItems.find((item) => item.key === parentKey) || line;
  }

  function keysForRemoval(cartItems, changedLine) {
    const line = bundleRemovalLine(cartItems, changedLine);
    const related = getRelatedLineKeys(cartItems, line) || [line.key];
    const keys = related.filter((key) => {
      const item = cartItems.find((entry) => entry.key === key);
      return item && !isNestedCartLine(item);
    });
    if (line?.key && !isNestedCartLine(line) && !keys.includes(line.key)) keys.unshift(line.key);
    return keys.length ? keys : [line.key];
  }

  const THRESHOLD_GIFT_RULES = [
    {
      giftHandle: 'dark-schoki-einzelriegel',
      parentHandles: ['notizbuch', 'oh-year-2027-familienplaner'],
      threshold: 2
    }
  ];

  function getLineProperties(line) {
    return line?.properties || {};
  }

  function isMarkedGiftLine(line) {
    const props = getLineProperties(line);
    return props._BundleGift === '1' || props._ProbeSetGift === '1';
  }

  function isZeroPriceLine(line) {
    return (parseInt(line?.final_line_price, 10) || 0) === 0;
  }

  function isLinkedInstanceGift(line) {
    const props = getLineProperties(line);
    if (props._BundleGiftParentProductId) return false;
    if (props._BundleGift === '1' && (props._BundleId || props._BundleInstance)) return true;
    if (props._ProbeSetGift === '1' && props._ProbeSetInstance) return true;
    return false;
  }

  function isThresholdGiftLine(line) {
    if (isNestedCartLine(line)) return false;
    if (isLinkedInstanceGift(line)) return false;
    const props = getLineProperties(line);
    if (props._BundleGift === '1' || props._BundleGiftParentProductId) return true;
    if (!isZeroPriceLine(line)) return false;
    return THRESHOLD_GIFT_RULES.some((rule) => rule.giftHandle === line.handle);
  }

  function isGiftOrFreeLine(line) {
    return isMarkedGiftLine(line) || isThresholdGiftLine(line) || isZeroPriceLine(line);
  }

  function sumParentQuantity(items, matches) {
    return items.reduce((sum, item) => {
      if (isNestedCartLine(item)) return sum;
      if (isMarkedGiftLine(item) || isThresholdGiftLine(item)) return sum;
      if (!matches(item)) return sum;
      return sum + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  function getLinkedGiftKeysWithoutParent(items) {
    const keys = [];
    items.forEach((item) => {
      if (isNestedCartLine(item)) return;
      const props = getLineProperties(item);
      if (props._BundleGift === '1' && (props._BundleId || props._BundleInstance) && !props._BundleGiftParentProductId) {
        const hasParent = items.some((other) => {
          if (other.key === item.key) return false;
          const otherProps = getLineProperties(other);
          if (otherProps._BundleGift) return false;
          if (props._BundleId && otherProps._BundleId === props._BundleId) return true;
          if (props._BundleInstance && otherProps._BundleInstance === props._BundleInstance) return true;
          return false;
        });
        if (!hasParent) keys.push(item.key);
      }
      if (props._ProbeSetGift === '1' && props._ProbeSetInstance) {
        const hasParent = items.some((other) => {
          if (other.key === item.key) return false;
          const otherProps = getLineProperties(other);
          return otherProps._ProbeSetInstance === props._ProbeSetInstance && !otherProps._ProbeSetGift;
        });
        if (!hasParent) keys.push(item.key);
      }
    });
    return keys;
  }

  function getOrphanGiftKeys(items) {
    const list = Array.isArray(items) ? items : [];
    const remove = new Set(getLinkedGiftKeysWithoutParent(list));

    const paidItems = list.filter((item) => {
      if (isNestedCartLine(item)) return false;
      if (isGiftOrFreeLine(item)) return false;
      const price = parseInt(item.price, 10) || 0;
      const final = parseInt(item.final_line_price, 10) || 0;
      return final > 0 || price > 0;
    });

    const taggedByParent = {};
    list.forEach((item) => {
      if (remove.has(item.key) || !isThresholdGiftLine(item)) return;
      const parentId = getLineProperties(item)._BundleGiftParentProductId;
      if (!parentId) return;
      if (!taggedByParent[parentId]) taggedByParent[parentId] = [];
      taggedByParent[parentId].push(item);
    });

    Object.entries(taggedByParent).forEach(([parentId, gifts]) => {
      const threshold = parseInt(getLineProperties(gifts[0])._BundleGiftThreshold, 10) || 2;
      const parentQty = sumParentQuantity(list, (item) => String(item.product_id) === String(parentId));
      const allowed = parentQty >= threshold ? Math.floor(parentQty / threshold) : 0;
      gifts.slice(allowed).forEach((gift) => remove.add(gift.key));
    });

    THRESHOLD_GIFT_RULES.forEach((rule) => {
      const gifts = list.filter((item) => {
        if (remove.has(item.key)) return false;
        if (item.handle !== rule.giftHandle) return false;
        const props = getLineProperties(item);
        if (props._BundleGiftParentProductId) return false;
        if (isLinkedInstanceGift(item)) return false;
        return isZeroPriceLine(item) || props._BundleGift === '1';
      });
      if (!gifts.length) return;
      const allowed = rule.parentHandles.reduce((sum, handle) => {
        const qty = sumParentQuantity(list, (item) => item.handle === handle);
        return sum + (qty >= rule.threshold ? Math.floor(qty / rule.threshold) : 0);
      }, 0);
      gifts.slice(allowed).forEach((gift) => remove.add(gift.key));
    });

    if (paidItems.length === 0) {
      list.forEach((item) => {
        if (isNestedCartLine(item)) return;
        if (isGiftOrFreeLine(item)) remove.add(item.key);
      });
    }

    return [...remove];
  }

  function projectCartItems(items, lineKey, targetQuantity, extraZeroKeys) {
    const zeroKeys = new Set(extraZeroKeys || []);
    return items
      .map((item) => {
        if (item.key === lineKey) {
          return Object.assign({}, item, { quantity: targetQuantity });
        }
        if (zeroKeys.has(item.key)) {
          return Object.assign({}, item, { quantity: 0 });
        }
        return item;
      })
      .filter((item) => (parseInt(item.quantity, 10) || 0) > 0);
  }

  function buildUpdates(keys, quantity) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = quantity;
    });
    return updates;
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async function updateCartLines(updates, sectionsToBundle) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionsToBundle
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to update cart');
    }

    return response.json();
  }

  function getSectionsToBundle() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );
    return sectionsToBundle;
  }

  function removeLineItemsFromDom(keys) {
    keys.forEach((key) => {
      const lineItem =
        document.querySelector(`line-item[data-line-key="${key}"]`) ||
        document.querySelector(`[data-line-key="${key}"]`)?.closest('line-item');
      if (lineItem) lineItem.remove();
    });
  }

  async function dispatchCartEvents(lineKey, lineItemEl, cartContent, targetQuantity) {
    await attachFreshDrawerSection(cartContent);
    const lineItemAfterChange = cartContent.items.filter((item) => item.key === lineKey);
    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:change', {
        bubbles: true,
        detail: {
          quantity:
            lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0].quantity,
          cart: cartContent
        }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'line-item:change',
          cart: cartContent
        }
      })
    );
  }

  async function postCartChange(lineKey, targetQuantity, sectionsToBundle) {
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: targetQuantity,
        sections: sectionsToBundle
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || 'Failed to change line item');
    }

    return response.json();
  }

  async function changeSingleLineQuantity(lineKey, targetQuantity, lineItemEl) {
    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:will-change', {
        bubbles: true,
        detail: { targetQuantity }
      })
    );

    const cartContent = await postCartChange(lineKey, targetQuantity, getSectionsToBundle());
    await dispatchCartEvents(lineKey, lineItemEl, cartContent, targetQuantity);
  }

  let giftReconcileLock = false;

  async function reconcileOrphanGifts(cart) {
    if (giftReconcileLock) return cart;
    const keys = getOrphanGiftKeys(cart?.items || []);
    if (!keys.length) return cart;

    giftReconcileLock = true;
    try {
      const cartContent = await updateCartLines(buildUpdates(keys, 0), getSectionsToBundle());
      await attachFreshDrawerSection(cartContent);
      removeLineItemsFromDom(keys);
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'gift:reconcile',
            cart: cartContent
          }
        })
      );
      return cartContent;
    } finally {
      giftReconcileLock = false;
    }
  }

  async function handleLineQuantityChange(lineKey, targetQuantity, lineItemEl) {
    const cart = await fetchCart();
    const changedLine = cart.items.find((item) => item.key === lineKey);
    if (!changedLine) return;

    const anchorLine = targetQuantity === 0 ? bundleRemovalLine(cart.items, changedLine) : changedLine;
    const removalKeys = targetQuantity === 0 ? keysForRemoval(cart.items, changedLine) : [lineKey];
    const projected = projectCartItems(
      cart.items,
      lineKey,
      targetQuantity,
      targetQuantity === 0 ? getRelatedLineKeys(cart.items, bundleRemovalLine(cart.items, changedLine)) || removalKeys : []
    );
    const giftKeys = getOrphanGiftKeys(projected).filter((key) => {
      const line = projected.find((item) => item.key === key) || cart.items.find((item) => item.key === key);
      return line && !isNestedCartLine(line);
    });
    const updates = buildUpdates(giftKeys, 0);
    if (targetQuantity === 0) {
      removalKeys.forEach((key) => {
        updates[key] = 0;
      });
    } else {
      updates[lineKey] = targetQuantity;
    }

    const updateKeys = Object.keys(updates);
    if (updateKeys.length === 1 && targetQuantity !== 0) {
      await changeSingleLineQuantity(lineKey, targetQuantity, lineItemEl);
      return;
    }

    lineItemEl?.dispatchEvent(
      new CustomEvent('line-item:will-change', {
        bubbles: true,
        detail: { targetQuantity }
      })
    );

    const sectionsToBundle = getSectionsToBundle();
    let cartContent;
    try {
      cartContent = await updateCartLines(updates, sectionsToBundle);
    } catch (error) {
      cartContent = await postCartChange(anchorLine?.key || lineKey, targetQuantity, sectionsToBundle);
    }
    if (updateKeys.length > 1) {
      removeLineItemsFromDom(updateKeys.filter((key) => updates[key] === 0));
    }

    await dispatchCartEvents(anchorLine?.key || lineKey, lineItemEl, cartContent, targetQuantity);
  }

  function initLineItemQuantityInterceptor() {
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('line-item-quantity a[href*="/cart/change"]');
        if (!link) return;

        const url = new URL(link.href);
        const quantity = parseInt(url.searchParams.get('quantity'), 10);
        if (quantity !== 0) return;

        const lineItemEl = link.closest('line-item');
        const lineKey = lineItemEl?.getAttribute('data-line-key') || url.searchParams.get('id');
        if (!lineKey) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
        handleLineQuantityChange(lineKey, 0, isCartPage ? null : lineItemEl).then(() => {
          if (isCartPage) window.location.reload();
        }).catch((error) => {
          console.error('Bundle cart removal failed:', error);
        });
      },
      true
    );

    document.addEventListener('change', (event) => {
      if (!event.target.hasAttribute('data-line-key')) return;
      if (event.target.closest('line-item-quantity') == null) return;

      const targetQuantity = parseInt(event.target.value, 10);
      if (Number.isNaN(targetQuantity) || targetQuantity < 0) return;

      const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
      event.stopImmediatePropagation();
      const lineKey = event.target.getAttribute('data-line-key');
      const lineItemEl = event.target.closest('line-item');

      handleLineQuantityChange(lineKey, targetQuantity, isCartPage ? null : lineItemEl)
        .then(() => {
          if (isCartPage) window.location.reload();
        })
        .catch((error) => {
          console.error('Bundle cart removal failed:', error);
        });
    }, true);
  }

  function parseBundlePricingAttribute(value) {
    const map = {};
    String(value || '')
      .split('|')
      .filter(Boolean)
      .forEach((entry) => {
        const [key, compare, final] = entry.split(':');
        if (!key) return;
        map[key] = {
          compare: parseInt(compare, 10) || 0,
          final: parseInt(final, 10) || 0
        };
      });
    return map;
  }

  function serializeBundlePricingAttribute(map) {
    return Object.entries(map || {})
      .filter(([, pricing]) => pricing && (pricing.compare > 0 || pricing.final > 0))
      .map(([key, pricing]) => `${key}:${pricing.compare}:${pricing.final}`)
      .join('|');
  }

  function lookupBundlePricing(map, bundleKey, bundleInstance) {
    if (!map) return null;
    if (bundleKey && map[bundleKey]) return map[bundleKey];
    if (bundleInstance && map[bundleInstance]) return map[bundleInstance];
    return null;
  }

  function formatBundleMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `${(cents / 100).toFixed(2).replace('.', ',')}\u00a0€`;
  }

  function resolveCartLineBundleKeys(cartLine) {
    const sources = [cartLine, ...(cartLine?.item_components || [])];
    let bundleId = '';
    let instanceId = '';
    let bundleGroup = '';

    sources.forEach((line) => {
      const props = line?.properties || {};
      if (!bundleId && props._BundleId) bundleId = props._BundleId;
      if (!instanceId && props._BundleInstance) instanceId = props._BundleInstance;
      if (!bundleGroup && props._BundleGroup) bundleGroup = props._BundleGroup;
    });

    if (!instanceId && bundleId) {
      instanceId = parseInstanceIdFromBundleId(bundleId, bundleGroup);
    }

    return { bundleId, instanceId };
  }

  function updateLineItemPriceElement(priceEl, pricing) {
    if (!priceEl || !pricing || pricing.compare <= pricing.final) return;

    priceEl.setAttribute('data-compare-cents', String(pricing.compare));
    priceEl.setAttribute('data-final-cents', String(pricing.final));

    const priceList = priceEl.querySelector('price-list');
    if (!priceList) return;

    const saleEl = priceList.querySelector('sale-price');
    if (!saleEl) return;

    let compareEl = priceList.querySelector('compare-at-price');
    const formattedFinal = formatBundleMoney(pricing.final);
    const formattedCompare = formatBundleMoney(pricing.compare);
    const saleSrOnly =
      saleEl.querySelector('.sr-only')?.outerHTML || '<span class="sr-only">Sale price</span>';
    const compareSrOnly =
      compareEl?.querySelector('.sr-only')?.outerHTML ||
      '<span class="sr-only">Regular price</span>';

    saleEl.className = 'text-on-sale';
    saleEl.innerHTML = `${saleSrOnly}${formattedFinal}`;

    if (!compareEl) {
      compareEl = document.createElement('compare-at-price');
      compareEl.className = 'text-subdued line-through';
      priceList.appendChild(compareEl);
    }

    compareEl.hidden = false;
    compareEl.innerHTML = `${compareSrOnly}${formattedCompare}`;
    syncLineSalePercent(priceEl);
  }

  function syncLineSalePercent(priceEl) {
    if (!priceEl || priceEl.getAttribute('data-line-sale-percent-enabled') !== 'true') return;
    const compare = parseInt(priceEl.getAttribute('data-compare-cents'), 10) || 0;
    const finalCents = parseInt(priceEl.getAttribute('data-final-cents'), 10) || 0;
    let badge = priceEl.querySelector('[data-line-sale-percent]');
    if (compare <= finalCents || compare <= 0) {
      if (badge) badge.hidden = true;
      return;
    }
    const percent = Math.round(((compare - finalCents) * 100) / compare);
    if (percent <= 0) {
      if (badge) badge.hidden = true;
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'badge badge--primary';
      badge.setAttribute('data-line-sale-percent', '');
      priceEl.appendChild(badge);
    }
    badge.hidden = false;
    badge.textContent = `-${percent}%`;
  }

  function getLineCustomCompareCents(props) {
    return parseInt(props?._BundleCustomCompareCents, 10) || 0;
  }

  function getLineListCompareCents(props) {
    return parseInt(props?._BundleListCompareCents, 10) || 0;
  }

  function resolveBundleCompareForLine(cartLine, map) {
    const props = cartLine?.properties || {};
    const customCompare = getLineCustomCompareCents(props);
    let listCompare = getLineListCompareCents(props);

    const { bundleId, instanceId } = resolveCartLineBundleKeys(cartLine);
    const attrPricing = lookupBundlePricing(map, bundleId, instanceId);
    if (attrPricing && (!listCompare || attrPricing.compare > listCompare)) {
      listCompare = attrPricing.compare || listCompare;
    }

    if (customCompare > 0) {
      listCompare = customCompare;
    }

    return listCompare;
  }

  function isNestedCartLine(line) {
    return Boolean(line?.parent_relationship?.parent || line?.parent_relationship?.parent_key);
  }

  function computeCartCompareTotalFromDom(cart, root) {
    const scope = root || document;
    const remainingKeys = new Set(
      (cart?.items || [])
        .filter((line) => !isNestedCartLine(line))
        .map((line) => line.key)
        .filter(Boolean)
    );
    let compareTotal = 0;
    let matched = 0;
    scope.querySelectorAll('.bk-media-line-item-prices').forEach((priceEl) => {
      const lineItem = priceEl.closest('line-item');
      if (!lineItem || lineItem.hidden) return;
      const key = lineItem.querySelector('[data-line-key]')?.getAttribute('data-line-key');
      if (remainingKeys.size && key && !remainingKeys.has(key)) return;
      const compare = parseInt(priceEl.getAttribute('data-compare-cents'), 10) || 0;
      const final = parseInt(priceEl.getAttribute('data-final-cents'), 10) || 0;
      compareTotal += Math.max(compare, final);
      matched += 1;
    });
    if (remainingKeys.size > 0 && matched !== remainingKeys.size) return 0;
    return compareTotal;
  }

  function computeCartCompareTotal(cart) {
    if (!Array.isArray(cart?.items)) return 0;

    const map = parseBundlePricingAttribute(cart?.attributes?._BundlePricing);
    const skippedKeys = new Set();
    const countedKeys = new Set();
    let compareTotal = 0;

    cart.items.forEach((parent) => {
      if (isNestedCartLine(parent)) return;
      const props = parent.properties || {};
      const isParent = Boolean(props._BundleParentVariantId);
      const customCompare = getLineCustomCompareCents(props);
      if (!isParent && customCompare <= 0) return;

      const compare = resolveBundleCompareForLine(parent, map);
      if (compare <= 0) return;

      compareTotal += compare;
      countedKeys.add(parent.key);

      const { bundleId, instanceId } = resolveCartLineBundleKeys(parent);
      cart.items.forEach((line) => {
        if (line.key === parent.key) return;
        const lineProps = line.properties || {};
        if (lineProps._BundleParentVariantId) return;
        if (bundleId && lineProps._BundleId === bundleId) {
          skippedKeys.add(line.key);
        } else if (instanceId && lineProps._BundleInstance === instanceId) {
          skippedKeys.add(line.key);
        }
      });
    });

    cart.items.forEach((line) => {
      if (isNestedCartLine(line)) return;
      if (skippedKeys.has(line.key) || countedKeys.has(line.key)) return;

      const lineFinal = parseInt(line.final_line_price, 10) || 0;
      const original = parseInt(line.original_line_price, 10) || lineFinal;
      let lineCompare = Math.max(lineFinal, original);

      const props = line.properties || {};
      const customCompare = getLineCustomCompareCents(props);
      const listCompare = getLineListCompareCents(props);
      if (customCompare > lineFinal) {
        lineCompare = customCompare;
      } else if (listCompare > lineFinal) {
        lineCompare = listCompare;
      }

      compareTotal += lineCompare;
    });

    return compareTotal;
  }

  function formatCartCompareDisplay(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount} €`;
  }

  function getCartDrawerSectionId(sectionId) {
    const drawer = document.querySelector('cart-drawer');
    const sectionEl = drawer?.closest('.shopify-section');
    if (sectionEl?.id) return sectionEl.id.replace('shopify-section-', '');
    const fromAttr = sectionId || drawer?.getAttribute('data-section-id');
    if (fromAttr && fromAttr !== 'cart-drawer') return fromAttr;
    return 'cart-drawer';
  }

  function drawerSectionMatchesCart(html, itemCount) {
    if (!html || typeof html !== 'string') return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const drawer = parsed.querySelector('#cart-drawer') || parsed.querySelector('cart-drawer');
    if (!drawer) return false;
    const count = parseInt(itemCount, 10) || 0;
    if (count === 0) return Boolean(drawer.querySelector('.empty-state'));
    const countEl = drawer.querySelector('.cart-drawer__top cart-count');
    const rendered = countEl ? parseInt(countEl.textContent, 10) : NaN;
    return rendered === count && Boolean(drawer.querySelector('.cart-drawer__line-items line-item'));
  }

  async function fetchCartDrawerSectionHtml(sectionId) {
    const id = getCartDrawerSectionId(sectionId);
    const response = await fetch(
      `${window.Shopify.routes.root}?section_id=${encodeURIComponent(id)}&sections_ts=${Date.now()}`,
      { cache: 'no-store' }
    );
    if (!response.ok) return '';
    return response.text();
  }

  async function attachFreshDrawerSection(cart) {
    if (!cart) return cart;
    const itemCount = parseInt(cart.item_count, 10) || 0;
    const sectionId = getCartDrawerSectionId();
    if (!cart.sections) cart.sections = {};
    if (drawerSectionMatchesCart(cart.sections[sectionId], itemCount)) return cart;

    let html = '';
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 200));
      try {
        html = await fetchCartDrawerSectionHtml(sectionId);
      } catch (error) {
        html = '';
      }
      if (drawerSectionMatchesCart(html, itemCount)) break;
    }

    if (html) cart.sections[sectionId] = html;
    return cart;
  }

  function replaceCartDrawerFromHtml(drawerHtml) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawerHtml) return false;
    const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const nextDrawer = parsed.querySelector('#cart-drawer') || parsed.querySelector('cart-drawer');
    if (!nextDrawer) return false;

    const currentInner = drawer.querySelector('.cart-drawer__inner');
    const nextInner = nextDrawer.querySelector('.cart-drawer__inner');
    const currentFooter = drawer.querySelector('[slot="footer"]');
    const nextFooter = nextDrawer.querySelector('[slot="footer"]');

    if (currentInner && nextInner) {
      currentInner.innerHTML = nextInner.innerHTML;
    }
    if (currentFooter && nextFooter) {
      currentFooter.replaceChildren(...nextFooter.childNodes);
    }
    return true;
  }

  async function refreshCartDrawerFromServer() {
    const drawerHtml = await fetchCartDrawerSectionHtml();
    if (!drawerHtml) return false;
    return replaceCartDrawerFromHtml(drawerHtml);
  }

  function applyCartDrawerFooterCompare(cart) {
    const savingsRow = document.querySelector('[data-cart-savings-row]');
    const savingsBadge = document.querySelector('[data-cart-savings-badge]');
    const compareEl = document.querySelector('[data-cart-total-compare]');
    const compareValue = document.querySelector('[data-cart-total-compare-value]');
    if (!savingsRow && !compareEl) return;

    const domCompare = computeCartCompareTotalFromDom(cart);
    const computed = cart ? computeCartCompareTotal(cart) : 0;
    const compareTotal = domCompare > 0 ? domCompare : computed;
    if (compareTotal <= 0) return;
    const cartTotal = parseInt(cart?.total_price, 10) || 0;
    const savings = Math.max(0, compareTotal - cartTotal);

    if (savings > 0) {
      if (savingsRow) savingsRow.hidden = false;
      if (savingsBadge) savingsBadge.textContent = formatCartCompareDisplay(savings);
      if (compareEl) compareEl.hidden = false;
      if (compareValue) compareValue.textContent = formatCartCompareDisplay(compareTotal);
    } else {
      if (savingsRow) savingsRow.hidden = true;
      if (compareEl) compareEl.hidden = true;
    }
  }

  function codeDiscountCents(cartLine) {
    const collect = (line) => {
      const allocations = line?.line_level_discount_allocations || [];
      let total = 0;
      allocations.forEach((allocation) => {
        const application = allocation.discount_application || {};
        const title = String(application.title || '').toLowerCase();
        const type = application.type || '';
        if (title.includes('gratis')) return;
        if (type !== 'discount_code' && type !== 'automatic') return;
        total += parseInt(allocation.amount, 10) || 0;
      });
      return total;
    };

    const parentDiscount = collect(cartLine);
    if (parentDiscount > 0) return parentDiscount;
    return (cartLine?.item_components || []).reduce(
      (sum, component) => sum + collect(component),
      0
    );
  }

  function readBundleBlockPricing(cartLine) {
    const sources = [cartLine, ...(cartLine?.item_components || [])];
    let compare = 0;
    let final = 0;
    sources.forEach((line) => {
      const props = line?.properties || {};
      const customCompare = getLineCustomCompareCents(props);
      const listCompare = getLineListCompareCents(props);
      const listFinal = parseInt(props._BundleListFinalCents, 10) || 0;
      const lineCompare = customCompare > 0 ? customCompare : listCompare;
      if (lineCompare > compare) compare = lineCompare;
      if (listFinal > final) final = listFinal;
    });
    return { compare, final };
  }

  function applyCartDrawerBundlePricing(cart) {
    const map = parseBundlePricingAttribute(cart?.attributes?._BundlePricing);

    if (Array.isArray(cart?.items)) {
      cart.items.forEach((cartLine) => {
        const props = cartLine.properties || {};
        const blockPricing = readBundleBlockPricing(cartLine);
        const { bundleId, instanceId } = resolveCartLineBundleKeys(cartLine);
        let pricing = lookupBundlePricing(map, bundleId, instanceId);
        const customCompare = blockPricing.compare;
        if (!props._BundleParentVariantId && !(cartLine.item_components || []).length && customCompare <= 0) {
          return;
        }
        let listFinal =
          blockPricing.final ||
          (pricing ? pricing.final : 0) ||
          parseInt(cartLine.final_line_price, 10) ||
          0;
        if (blockPricing.final > 0) {
          const discount = codeDiscountCents(cartLine);
          if (discount > 0) listFinal = Math.max(0, blockPricing.final - discount);
        }

        if (customCompare > listFinal) {
          pricing = {
            compare: customCompare,
            final: listFinal || (pricing ? pricing.final : 0)
          };
        }

        if (!pricing || pricing.compare <= pricing.final) return;

        const lineItemEl = document
          .querySelector(`line-item input[data-line-key="${cartLine.key}"]`)
          ?.closest('line-item');
        if (!lineItemEl) return;

        const priceEl = lineItemEl.querySelector('.bk-media-line-item-prices');
        if (!priceEl) return;

        if (bundleId) {
          priceEl.setAttribute('data-bundle-pricing-key', bundleId);
        }
        if (instanceId) {
          priceEl.setAttribute('data-bundle-pricing-instance', instanceId);
        }

        updateLineItemPriceElement(priceEl, pricing);
      });
    }

    applyCartDrawerFooterCompare(cart);
  }

  async function persistBundleCartPricing({
    bundleId,
    instanceId,
    compareCents,
    finalCents,
    cart: existingCart
  }) {
    const compare = parseInt(compareCents, 10) || 0;
    const final = parseInt(finalCents, 10) || 0;
    if (compare <= final || final <= 0) return null;

    const cart = existingCart || (await fetchCart());
    const map = parseBundlePricingAttribute(cart.attributes?._BundlePricing);
    const entry = { compare, final };

    if (bundleId) map[bundleId] = entry;
    if (instanceId) map[instanceId] = entry;

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          ...(cart.attributes || {}),
          _BundlePricing: serializeBundlePricingAttribute(map)
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to persist bundle pricing');
    }

    return response.json();
  }

  function scheduleCartDrawerPricingApply(cart) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => applyCartDrawerBundlePricing(cart));
    });
  }

  let cartDrawerRefreshTimer = null;

  function initBundleCartPricing() {
    document.addEventListener('cart:change', (event) => {
      const cart = event.detail?.cart;
      if (!cart) return;

      if (event.detail?.baseEvent === 'variant:add') {
        clearTimeout(cartDrawerRefreshTimer);
        scheduleCartDrawerPricingApply(cart);
        return;
      }

      if (event.detail?.baseEvent !== 'gift:reconcile') {
        reconcileOrphanGifts(cart).catch((error) => {
          console.error('Gift cart reconcile failed:', error);
        });
      }

      clearTimeout(cartDrawerRefreshTimer);
      cartDrawerRefreshTimer = setTimeout(async () => {
        const freshCart = await fetchCart().catch(() => cart);
        applyCartDrawerBundlePricing(freshCart);
      }, 1300);
    });

    fetchCart()
      .then((cart) => {
        scheduleCartDrawerPricingApply(cart);
        return reconcileOrphanGifts(cart);
      })
      .catch(() => {});
  }

  window.BkMediaBundleCartPricing = {
    parseAttribute: parseBundlePricingAttribute,
    serializeAttribute: serializeBundlePricingAttribute,
    persist: persistBundleCartPricing,
    applyCartDrawerPrices: applyCartDrawerBundlePricing,
    fetchDrawerSectionHtml: fetchCartDrawerSectionHtml,
    attachFreshDrawerSection: attachFreshDrawerSection,
    refreshDrawerFromServer: refreshCartDrawerFromServer
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initLineItemQuantityInterceptor();
      initBundleCartPricing();
    }, { once: true });
  } else {
    initLineItemQuantityInterceptor();
    initBundleCartPricing();
  }
})();
